import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UploadSummary = ({ 
  files = [], 
  onProcessFiles, 
  isProcessing = false 
}) => {
  const totalFiles = files?.length;
  const completedFiles = files?.filter(f => f?.status === 'completed')?.length;
  const errorFiles = files?.filter(f => f?.status === 'error')?.length;
  const processingFiles = files?.filter(f => f?.status === 'uploading' || f?.status === 'processing')?.length;

  const totalSize = files?.reduce((sum, file) => sum + (file?.size || 0), 0);
  const canProcess = completedFiles > 0 && processingFiles === 0;

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const getOverallStatus = () => {
    if (processingFiles > 0) return { text: 'Processing...', color: 'text-warning' };
    if (errorFiles > 0 && completedFiles === 0) return { text: 'Upload Failed', color: 'text-error' };
    if (errorFiles > 0) return { text: 'Partial Success', color: 'text-warning' };
    if (completedFiles > 0) return { text: 'Ready to Process', color: 'text-success' };
    return { text: 'No Files', color: 'text-muted-foreground' };
  };

  const status = getOverallStatus();

  if (totalFiles === 0) {
    return null;
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Upload Summary</h3>
        <div className={`flex items-center space-x-2 ${status?.color}`}>
          <Icon 
            name={processingFiles > 0 ? "Loader2" : completedFiles > 0 ? "CheckCircle" : "AlertCircle"} 
            size={16} 
            className={processingFiles > 0 ? "animate-spin" : ""}
          />
          <span className="text-sm font-medium">{status?.text}</span>
        </div>
      </div>
      {/* Statistics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center p-3 bg-muted rounded-lg">
          <div className="text-2xl font-bold text-foreground">{totalFiles}</div>
          <div className="text-xs text-muted-foreground">Total Files</div>
        </div>
        
        <div className="text-center p-3 bg-success bg-opacity-10 border border-success border-opacity-20 rounded-lg">
          <div className="text-2xl font-bold text-success">{completedFiles}</div>
          <div className="text-xs text-muted-foreground">Ready</div>
        </div>
        
        {processingFiles > 0 && (
          <div className="text-center p-3 bg-warning bg-opacity-10 border border-warning border-opacity-20 rounded-lg">
            <div className="text-2xl font-bold text-warning">{processingFiles}</div>
            <div className="text-xs text-muted-foreground">Processing</div>
          </div>
        )}
        
        {errorFiles > 0 && (
          <div className="text-center p-3 bg-error bg-opacity-10 border border-error border-opacity-20 rounded-lg">
            <div className="text-2xl font-bold text-error">{errorFiles}</div>
            <div className="text-xs text-muted-foreground">Failed</div>
          </div>
        )}
        
        <div className="text-center p-3 bg-muted rounded-lg">
          <div className="text-lg font-bold text-foreground">{formatFileSize(totalSize)}</div>
          <div className="text-xs text-muted-foreground">Total Size</div>
        </div>
      </div>
      {/* File Breakdown */}
      {totalFiles > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-foreground mb-3">File Breakdown</h4>
          <div className="space-y-2">
            {files?.slice(0, 3)?.map((file) => (
              <div key={file?.id} className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  <Icon 
                    name={file?.name?.endsWith('.csv') ? 'FileText' : 'FileSpreadsheet'} 
                    size={14} 
                    className="text-muted-foreground flex-shrink-0" 
                  />
                  <span className="text-foreground truncate" title={file?.name}>
                    {file?.name}
                  </span>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <span className="text-xs text-muted-foreground">
                    {formatFileSize(file?.size)}
                  </span>
                  {file?.status === 'completed' && (
                    <Icon name="CheckCircle" size={12} className="text-success" />
                  )}
                  {file?.status === 'error' && (
                    <Icon name="AlertCircle" size={12} className="text-error" />
                  )}
                  {(file?.status === 'uploading' || file?.status === 'processing') && (
                    <Icon name="Loader2" size={12} className="text-warning animate-spin" />
                  )}
                </div>
              </div>
            ))}
            {totalFiles > 3 && (
              <div className="text-xs text-muted-foreground text-center pt-2">
                +{totalFiles - 3} more files...
              </div>
            )}
          </div>
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="default"
          size="lg"
          onClick={onProcessFiles}
          disabled={!canProcess || isProcessing}
          loading={isProcessing}
          iconName="Play"
          iconPosition="left"
          fullWidth
          className="sm:flex-1"
        >
          {isProcessing ? 'Processing Files...' : `Process ${completedFiles} File${completedFiles !== 1 ? 's' : ''}`}
        </Button>
        
        <Button
          variant="outline"
          size="lg"
          onClick={() => window.location.href = '/column-mapping-interface'}
          disabled={completedFiles === 0}
          iconName="Settings"
          iconPosition="left"
          className="sm:w-auto"
        >
          Configure Mapping
        </Button>
      </div>
      {/* Status Messages */}
      {errorFiles > 0 && (
        <div className="mt-4 p-3 bg-error bg-opacity-10 border border-error border-opacity-20 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-error mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-error font-medium">
                {errorFiles} file{errorFiles !== 1 ? 's' : ''} failed to upload
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Check the file queue above for error details and retry if needed.
              </p>
            </div>
          </div>
        </div>
      )}
      {processingFiles > 0 && (
        <div className="mt-4 p-3 bg-warning bg-opacity-10 border border-warning border-opacity-20 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="Clock" size={16} className="text-warning mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-warning font-medium">
                Upload in progress...
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Please wait for all files to complete before processing.
              </p>
            </div>
          </div>
        </div>
      )}
      {canProcess && (
        <div className="mt-4 p-3 bg-success bg-opacity-10 border border-success border-opacity-20 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-success font-medium">
                Files ready for processing
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Click "Process Files" to start the AI-powered product matching analysis.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadSummary;